// NotesApp source file: filter.service.ts

import { Injectable } from '@angular/core';

export interface SavedTagFilter {
  id: string;
  name: string;
  primaryTag?: string;
  includeTags: string[];
  excludeTags: string[];
  searchText?: string;
  viewMode: 'flat' | 'grouped';
  sortBy?: 'updatedDesc' | 'titleAsc' | 'createdDesc';
  createdAt: string;
  updatedAt: string;
}

/** Represents a saved search/filter configuration with a user-facing name. */
export interface SavedSearch {
  id: string; // unique identifier (e.g., timestamp-based)
  name: string; // user-provided name
  displayOrder?: number; // user-defined ordering position (1-based)
  searchString?: string;
  searchType?: string;
  tagIds?: string[];
  tagIncludeMode?: 'AND' | 'OR';
  excludeTagIds?: string[];
  categories?: string[];
  type?: string;
  projectName?: string;
  status?: string;
  modifiedBetween?: { start: Date | null; end: Date | null };
  createdBetween?: { start: Date | null; end: Date | null };
  dateFilterField?: 'modified' | 'created';
  dateFilterMode?: 'days' | 'date';
  dateSinceDays?: number | null;
  dateSinceDate?: Date | null;
  createdAt?: Date; // timestamp when the saved search was created
  /** Optional payload used by tag explorer view. */
  tagExplorer?: SavedTagFilter;
}

export interface FilterParams {
  Status?: string;
  Type?: string;
  ProjectName?: string;
  SearchString?: string;
  SearchType?: string;
  SemanticTopK?: number;
  /** Tags the note MUST contain (AND logic — all selected tags required). */
  TagIds?: string[];
  /** Include-tag operator: AND requires all, OR requires any selected include tag. */
  TagIncludeMode?: 'AND' | 'OR';
  /** Tags the note must NOT contain (OR logic — any match removes the note). */
  ExcludeTagIds?: string[];
  Categories?: string[];
  ModifiedBetween?: { start: Date | null; end: Date | null };
  CreatedBetween?: { start: Date | null; end: Date | null };
  DateFilterField?: 'modified' | 'created';
  DateFilterMode?: 'days' | 'date';
  DateSinceDays?: number | null;
  DateSinceDate?: Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private filter: FilterParams = {};

  setFilterParams(params: FilterParams) {
    this.filter = params;
  }

  getFilterParams(): FilterParams {
    return this.filter;
  }

  getFilterParamsAsString(): string {
    const {
      Status,
      Type,
      ProjectName,
      SearchString,
      SearchType,
      TagIds,
      TagIncludeMode,
      ExcludeTagIds,
      Categories,
      ModifiedBetween,
      CreatedBetween,
      DateFilterField,
      DateFilterMode,
      DateSinceDays,
      DateSinceDate,
    } =
      this.filter;

    const fmt = (d?: Date | null) => (d instanceof Date && !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '∅');

    const parts = [
      Status ? `Status: ${Status}` : '',
      Type ? `Type: ${Type}` : '',
      ProjectName ? `ProjectName: ${ProjectName}` : '',
      SearchString ? `SearchString: ${SearchString}` : '',
      SearchType ? `SearchType: ${SearchType}` : '',
      TagIds && TagIds.length ? `TagIds: ${TagIds.join(', ')}` : '',
      TagIncludeMode ? `TagIncludeMode: ${TagIncludeMode}` : '',
      ExcludeTagIds && ExcludeTagIds.length ? `ExcludeTagIds: ${ExcludeTagIds.join(', ')}` : '',
      Categories && Categories.length ? `Categories: ${Categories.join(', ')}` : '',
      DateFilterField ? `DateFilterField: ${DateFilterField}` : '',
      DateFilterMode ? `DateFilterMode: ${DateFilterMode}` : '',
      DateSinceDays && DateSinceDays > 0 ? `DateSinceDays: ${DateSinceDays}` : '',
      DateSinceDate ? `DateSinceDate: ${fmt(DateSinceDate)}` : '',
      ModifiedBetween ? `ModifiedBetween: ${fmt(ModifiedBetween.start)} - ${fmt(ModifiedBetween.end)}` : '',
      CreatedBetween ? `CreatedBetween: ${fmt(CreatedBetween.start)} - ${fmt(CreatedBetween.end)}` : '',
    ].filter(Boolean); // removes falsy values like ''

    return parts.join(', ');
  }
}
